import { useCallback, useMemo, useState } from 'react';

export interface Option {
  value: string;
  label: string;
}

export interface OptionCache {
  options: Option[];
  addToCache: (optionsToAdd: Option[]) => void;
}

export function useOptionCache(initialOptions?: Option[]): OptionCache {
  const [initialOptionsMemoized] = useState<Option[]>(initialOptions ?? []);
  const [options, setOptions] = useState<Option[]>(initialOptionsMemoized);

  const addToCache = useCallback(
    (optionsToAdd: Option[]) => {
      setOptions((currentOptions) => {
        const uniqueOptionsByKey = Object.fromEntries(
          [...currentOptions, ...optionsToAdd].map((option) => [
            option.value,
            option,
          ]),
        );

        const uniqueOptions = Object.values(uniqueOptionsByKey);
        if (uniqueOptions.length === currentOptions.length) {
          return currentOptions;
        }

        return Array.from(uniqueOptions);
      });
    },
    [setOptions],
  );

  return useMemo(() => {
    return { options, addToCache };
  }, [addToCache, options]);
}
